import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  FileText,
  Calendar,
  DollarSign,
  CreditCard
} from 'lucide-react';
import { apiService } from '../services/api';
import { CreditMemoDto } from '../types';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const CreditMemosPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Confirmation modal state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState<CreditMemoDto | null>(null);

  // Fetch credit memos
  const { data: creditMemosData, isLoading, error } = useQuery({
    queryKey: ['credit-memos', searchTerm, statusFilter],
    queryFn: () => apiService.getCreditMemos(1, 100, searchTerm),
  });

  // Apply credit memo mutation
  const applyCreditMemoMutation = useMutation({
    mutationFn: (id: number) => apiService.applyCreditMemo(id),
    onSuccess: () => {
      toast.success('Credit memo applied successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setIsApplyModalOpen(false);
      setSelectedCreditMemo(null);
    },
    onError: (error: any) => {
      toast.error('Failed to apply credit memo');
      console.error('Error applying credit memo:', error);
    }
  });

  // Cancel credit memo mutation
  const cancelCreditMemoMutation = useMutation({
    mutationFn: (id: number) => apiService.cancelCreditMemo(id),
    onSuccess: () => {
      toast.success('Credit memo cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setIsCancelModalOpen(false);
      setSelectedCreditMemo(null);
    },
    onError: (error: any) => {
      toast.error('Failed to cancel credit memo');
      console.error('Error cancelling credit memo:', error);
    }
  });

  // Delete credit memo mutation
  const deleteCreditMemoMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteCreditMemo(id),
    onSuccess: () => {
      toast.success('Credit memo deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['credit-memos'] });
      setIsDeleteModalOpen(false);
      setSelectedCreditMemo(null);
    },
    onError: (error: any) => {
      toast.error('Failed to delete credit memo');
      console.error('Error deleting credit memo:', error);
    }
  });

  // Filter credit memos
  const filteredCreditMemos = useMemo(() => {
    if (!creditMemosData?.creditMemos) return [];
    
    let filtered = creditMemosData.creditMemos;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter((cm: CreditMemoDto) => cm.status === statusFilter);
    }
    
    return filtered;
  }, [creditMemosData?.creditMemos, statusFilter]);

  const handleViewDetails = (creditMemo: CreditMemoDto) => {
    navigate(`/credit-memos/${creditMemo.id}`);
  };

  const handleEditCreditMemo = (creditMemo: CreditMemoDto) => {
    navigate(`/credit-memos/${creditMemo.id}/edit`);
  };

  const handleApplyCreditMemo = (creditMemo: CreditMemoDto) => {
    setSelectedCreditMemo(creditMemo);
    setIsApplyModalOpen(true);
  };

  const handleCancelCreditMemo = (creditMemo: CreditMemoDto) => {
    setSelectedCreditMemo(creditMemo);
    setIsCancelModalOpen(true);
  };

  const handleDeleteCreditMemo = (creditMemo: CreditMemoDto) => {
    setSelectedCreditMemo(creditMemo);
    setIsDeleteModalOpen(true);
  };

  const confirmApply = () => {
    if (selectedCreditMemo) {
      applyCreditMemoMutation.mutate(selectedCreditMemo.id);
    }
  };

  const confirmCancel = () => {
    if (selectedCreditMemo) {
      cancelCreditMemoMutation.mutate(selectedCreditMemo.id);
    }
  };

  const confirmDelete = () => {
    if (selectedCreditMemo) {
      deleteCreditMemoMutation.mutate(selectedCreditMemo.id);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'Draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      'Applied': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status as keyof typeof statusClasses] || statusClasses.Draft}`}>
        {status}
      </span>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading credit memos</div>
          <div className="text-gray-600 dark:text-gray-400">{error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2">
                <CreditCard className="h-6 w-6 text-gray-900 dark:text-gray-100" />
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Credit Memos</h1>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage credit memos for invoices</p>
            </div>
            <button
              onClick={() => navigate('/credit-memos/create')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-color-600 hover:bg-accent-color-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Credit Memo
            </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  id="search"
                  placeholder="Search by credit memo number, invoice number, or reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color-500 focus:border-accent-color-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Applied">Applied</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Credit Memos Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading credit memos...</p>
            </div>
          ) : filteredCreditMemos.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No credit memos found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search criteria or filters.'
                  : 'Get started by creating your first credit memo.'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={() => navigate('/credit-memos/create')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-color-600 hover:bg-accent-color-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Credit Memo
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Credit Memo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Original Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredCreditMemos.map((creditMemo: CreditMemoDto) => (
                    <tr key={creditMemo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {creditMemo.creditMemoNumber}
                          </div>
                          {creditMemo.reason && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {creditMemo.reason}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {creditMemo.originalInvoiceNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          ${creditMemo.total.toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {creditMemo.items.length} item{creditMemo.items.length !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(creditMemo.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(creditMemo.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewDetails(creditMemo)}
                            className="text-accent-color-600 hover:text-accent-color-900 dark:text-accent-color-400 dark:hover:text-accent-color-300"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {creditMemo.status === 'Draft' && (
                            <>
                              <button
                                onClick={() => handleEditCreditMemo(creditMemo)}
                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleApplyCreditMemo(creditMemo)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                title="Apply Credit Memo"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteCreditMemo(creditMemo)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          
                          {creditMemo.status === 'Applied' && (
                            <button
                              onClick={() => handleCancelCreditMemo(creditMemo)}
                              className="text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300"
                              title="Cancel Credit Memo"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Apply Confirmation Modal */}
      <ConfirmDialog
        isOpen={isApplyModalOpen}
        onClose={() => {
          setIsApplyModalOpen(false);
          setSelectedCreditMemo(null);
        }}
        onConfirm={confirmApply}
        title="Apply Credit Memo"
        message={`Are you sure you want to apply credit memo "${selectedCreditMemo?.creditMemoNumber}"?`}
        confirmText="Apply"
        cancelText="Cancel"
        variant="info"
        isLoading={applyCreditMemoMutation.isPending}
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmDialog
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setSelectedCreditMemo(null);
        }}
        onConfirm={confirmCancel}
        title="Cancel Credit Memo"
        message={`Are you sure you want to cancel credit memo "${selectedCreditMemo?.creditMemoNumber}"?`}
        confirmText="Cancel Credit Memo"
        cancelText="Keep Active"
        variant="warning"
        isLoading={cancelCreditMemoMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCreditMemo(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Credit Memo"
        message={`Are you sure you want to delete credit memo "${selectedCreditMemo?.creditMemoNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteCreditMemoMutation.isPending}
      />
    </div>
  );
};

export default CreditMemosPage;
